import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { superadminApi } from '../lib/api';

const SANS = "'Roboto', system-ui, sans-serif";

const CURRENCIES = ['USD', 'GBP', 'EUR', 'INR', 'AUD'];
const FEE_MODELS = [
  { v: 'fixed_retainer', l: 'Fixed retainer' },
  { v: 'percent_of_savings', l: '% of savings' },
  { v: 'hybrid', l: 'Hybrid (retainer + variable)' },
  { v: 'one_time', l: 'One-time project fee' },
];
const PAYMENT_TERMS = [
  { v: 'net_30', l: 'Net 30' },
  { v: 'net_45', l: 'Net 45' },
  { v: 'quarterly', l: 'Quarterly' },
  { v: 'annual_upfront', l: 'Annual upfront' },
];
const CADENCES = [
  { v: 'quarterly', l: 'Quarterly' },
  { v: 'semi_annual', l: 'Semi-annual' },
  { v: 'annual', l: 'Annual' },
];
const ROLES = [
  { v: 'cfo', l: 'CFO' },
  { v: 'procurement_head', l: 'Procurement Head' },
  { v: 'procurement_manager', l: 'Procurement Manager' },
];

interface Props {
  tenantId: string;
  tenantName: string;
  prospectContactEmail?: string | null;
  onClose: () => void;
  onSuccess: (msg: string) => void;
}

export default function PromoteToCustomerModal({
  tenantId, tenantName, prospectContactEmail, onClose, onSuccess,
}: Props) {
  const [f, setF] = useState({
    portalAdminEmail: prospectContactEmail ?? '',
    portalAdminRole: 'cfo',
    portalAdminName: '',
    guaranteedSavingsAmount: '',
    guaranteedSavingsCurrency: 'USD',
    spendUnderManagement: '',
    spendUnderManagementCurrency: 'USD',
    engagementFeeModel: 'fixed_retainer',
    engagementFeeValue: '',
    engagementStartDate: '',
    engagementEndDate: '',
    paymentTerms: 'net_30',
    vendorsInScopeCount: '',
    categoriesInScope: '',
    benchmarkRefreshCadence: 'quarterly',
    msaDocumentUrl: '',
    signedByEmail: '',
    invitationExpiresInDays: '7',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    if (!done) return;
    onSuccess(done);
    const t = setTimeout(onClose, 2600);
    return () => clearTimeout(t);
  }, [done, onClose, onSuccess]);

  const set = (k: string, v: string) => setF((s) => ({ ...s, [k]: v }));

  function validate(): string | null {
    if (!f.portalAdminEmail.includes('@')) return 'Portal admin email is required and must be valid';
    if (!f.signedByEmail.includes('@')) return 'Signed-by email is required and must be valid';
    const nums: Array<[string, string]> = [
      ['Guaranteed savings amount', f.guaranteedSavingsAmount],
      ['Spend under management', f.spendUnderManagement],
      ['Engagement fee value', f.engagementFeeValue],
      ['Vendors in scope count', f.vendorsInScopeCount],
    ];
    for (const [label, val] of nums) {
      if (val.trim() === '' || !Number.isFinite(Number(val))) return `${label} must be a number`;
      if (Number(val) < 0) return `${label} must be non-negative`;
    }
    if (!f.engagementStartDate) return 'Engagement start date is required';
    if (!f.categoriesInScope.trim()) return 'At least one category in scope is required';
    return null;
  }

  async function submit() {
    const v = validate();
    if (v) { setError(v); return; }
    setSubmitting(true); setError(null);
    try {
      const categories = f.categoriesInScope.split(',').map((s) => s.trim()).filter(Boolean);
      const res = await superadminApi.promoteToCustomer(tenantId, {
        portalAdminEmail: f.portalAdminEmail.trim(),
        portalAdminRole: f.portalAdminRole,
        portalAdminName: f.portalAdminName.trim() || undefined,
        guaranteedSavingsAmount: Number(f.guaranteedSavingsAmount),
        guaranteedSavingsCurrency: f.guaranteedSavingsCurrency,
        spendUnderManagement: Number(f.spendUnderManagement),
        spendUnderManagementCurrency: f.spendUnderManagementCurrency,
        engagementFeeModel: f.engagementFeeModel,
        engagementFeeValue: Number(f.engagementFeeValue),
        engagementStartDate: f.engagementStartDate,
        engagementEndDate: f.engagementEndDate || null,
        paymentTerms: f.paymentTerms,
        vendorsInScopeCount: Number(f.vendorsInScopeCount),
        categoriesInScope: categories,
        benchmarkRefreshCadence: f.benchmarkRefreshCadence,
        msaDocumentUrl: f.msaDocumentUrl.trim() || null,
        signedByEmail: f.signedByEmail.trim(),
        invitationExpiresInDays: Number(f.invitationExpiresInDays) || undefined,
      });
      setDone(res.message ?? 'Tenant promoted to customer.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Promote failed');
    } finally {
      setSubmitting(false);
    }
  }

  const field = (label: string, node: React.ReactNode) => (
    <div style={{ marginBottom: '0.85rem' }}>
      <p style={labelStyle}>{label}</p>
      {node}
    </div>
  );
  const txt = (k: keyof typeof f, ph = '') => (
    <input value={f[k]} onChange={(e) => set(k, e.target.value)} placeholder={ph} style={inputStyle} />
  );
  const sel = (k: keyof typeof f, opts: Array<{ v: string; l: string }>) => (
    <select value={f[k]} onChange={(e) => set(k, e.target.value)} style={selectStyle}>
      {opts.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  );
  const cur = (k: keyof typeof f) => (
    <select value={f[k]} onChange={(e) => set(k, e.target.value)} style={selectStyle}>
      {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
    </select>
  );

  return createPortal(
    <div style={overlayStyle} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modalStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <div>
            <p style={{ margin: 0, fontSize: '1rem', fontWeight: 400, color: '#0f172a' }}>Promote to Customer</p>
            <p style={{ margin: '0.2rem 0 0', fontSize: '0.75rem', color: '#64748b' }}>
              {tenantName} — creates the engagement record and emails the portal admin an invitation.
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '1.2rem', lineHeight: 1, padding: '0.2rem' }}>×</button>
        </div>

        {done ? (
          <div style={{ backgroundColor: '#d1fae5', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 8, padding: '0.85rem 1rem' }}>
            <p style={{ margin: '0 0 0.25rem', fontSize: '0.875rem', color: '#065f46' }}>✅ {done}</p>
            <p style={{ margin: 0, fontSize: '0.78rem', color: '#64748b' }}>Closing…</p>
          </div>
        ) : (
          <>
            <div style={{ backgroundColor: '#fef3c7', border: '1px solid rgba(217,119,6,0.3)', borderRadius: 8, padding: '0.7rem 0.85rem', marginBottom: '1rem' }}>
              <p style={{ margin: 0, fontSize: '0.76rem', color: '#92400e' }}>
                This is irreversible. It creates a permanent engagement record and sends a real login invitation. Confirm all terms match the signed agreement before submitting.
              </p>
            </div>

            {field('Portal Admin Email *', txt('portalAdminEmail', 'cfo@company.com'))}
            {field('Portal Admin Role *', sel('portalAdminRole', ROLES))}
            {field('Portal Admin Name', txt('portalAdminName', 'optional'))}

            <div style={{ display: 'flex', gap: '0.6rem' }}>
              <div style={{ flex: 2 }}>{field('Guaranteed Savings *', txt('guaranteedSavingsAmount', '250000'))}</div>
              <div style={{ flex: 1 }}>{field('Cur', cur('guaranteedSavingsCurrency'))}</div>
            </div>
            <div style={{ display: 'flex', gap: '0.6rem' }}>
              <div style={{ flex: 2 }}>{field('Spend Under Mgmt *', txt('spendUnderManagement', '2000000'))}</div>
              <div style={{ flex: 1 }}>{field('Cur', cur('spendUnderManagementCurrency'))}</div>
            </div>

            {field('Engagement Fee Model *', sel('engagementFeeModel', FEE_MODELS))}
            {field('Engagement Fee Value *', txt('engagementFeeValue', 'amount or percent'))}
            {field('Engagement Start Date *', <input type="date" value={f.engagementStartDate} onChange={(e) => set('engagementStartDate', e.target.value)} style={inputStyle} />)}
            {field('Engagement End Date', <input type="date" value={f.engagementEndDate} onChange={(e) => set('engagementEndDate', e.target.value)} style={inputStyle} />)}
            {field('Payment Terms *', sel('paymentTerms', PAYMENT_TERMS))}
            {field('Vendors In Scope (count) *', txt('vendorsInScopeCount', '13'))}
            {field('Categories In Scope * (comma-separated)', txt('categoriesInScope', 'Cybersecurity, Productivity, HR & People'))}
            {field('Benchmark Refresh Cadence *', sel('benchmarkRefreshCadence', CADENCES))}
            {field('MSA Document URL', txt('msaDocumentUrl', 'optional'))}
            {field('Signed By Email *', txt('signedByEmail', 'who signed the engagement'))}
            {field('Invitation Expires (days)', txt('invitationExpiresInDays', '7'))}

            {error && (
              <div style={{ backgroundColor: '#fef2f2', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '0.6rem 0.85rem', marginBottom: '0.85rem' }}>
                <p style={{ margin: 0, fontSize: '0.78rem', color: '#b91c1c' }}>{error}</p>
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.6rem' }}>
              <button onClick={onClose} disabled={submitting} style={{ flex: 1, padding: '0.6rem', borderRadius: 8, border: '1px solid #e2e8f0', background: 'none', color: '#64748b', fontSize: '0.85rem', cursor: 'pointer', fontFamily: SANS }}>
                Cancel
              </button>
              <button onClick={submit} disabled={submitting} style={{ flex: 2, padding: '0.6rem', borderRadius: 8, border: 'none', background: submitting ? '#94a3b8' : '#0ea5e9', color: '#fff', fontSize: '0.85rem', fontWeight: 500, cursor: submitting ? 'default' : 'pointer', fontFamily: SANS }}>
                {submitting ? 'Promoting…' : 'Promote to Customer'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000,
  backgroundColor: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)',
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
  padding: '60px 20px', overflowY: 'auto',
};
const modalStyle: React.CSSProperties = {
  backgroundColor: '#ffffff', border: '1px solid #e2e8f0',
  borderRadius: 14, padding: '2rem', width: '100%', maxWidth: '480px',
  maxHeight: 'calc(100vh - 120px)', overflowY: 'auto',
  display: 'flex', flexDirection: 'column',
  fontFamily: SANS, boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
};
const labelStyle: React.CSSProperties = {
  margin: '0 0 0.4rem', fontSize: '0.72rem', fontWeight: 500, color: '#64748b',
  textTransform: 'uppercase', letterSpacing: '0.06em',
};
const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', backgroundColor: '#ffffff', border: '1px solid #e2e8f0',
  borderRadius: 8, padding: '0.45rem 0.75rem', color: '#0f172a', fontSize: '0.85rem',
  outline: 'none', fontFamily: SANS,
};
const selectStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', backgroundColor: '#ffffff', border: '1px solid #e2e8f0',
  borderRadius: 8, padding: '0.45rem 0.75rem', color: '#0f172a', fontSize: '0.85rem',
  cursor: 'pointer', outline: 'none', fontFamily: SANS,
};
